import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React, {
  Children,
  cloneElement,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from 'react';

interface TabsContextType {
  value: number;
  setValue: (val: number) => void;
}

export const TabsContext = createContext<TabsContextType | undefined>(
  undefined
);

interface TabsRootProps {
  defaultValue?: number;
  children: ReactNode;
  [key: string]: unknown;
}

// Опишем интерфейс compound component с вложенными компонентами
interface TabsRootComponent extends FC<TabsRootProps> {
  TabList: FC<{ children: ReactNode; [key: string]: unknown }>;
  Tab: FC<{
    children: ReactNode;
    value?: number;
    id?: string;
    'aria-controls'?: string;
    [key: string]: unknown;
  }>;
  TabPanel: FC<{ children?: ReactNode; value: number; [key: string]: unknown }>;
}

const TabsRoot: TabsRootComponent = function TabsRoot({
  defaultValue = 0,
  children,
  ...props
}) {
  const [value, setValue] = useState(defaultValue);

  return (
    <TabsContext.Provider value={{ value, setValue }}>
      <Box sx={{ width: '100%', padding: '0px' }} {...props}>
        {children}
      </Box>
    </TabsContext.Provider>
  );
};

// Вложенный компонент TabList
TabsRoot.TabList = function TabList({ children, ...other }) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsRoot.TabList must be used within a TabsRoot');
  }

  const { value, setValue } = context;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const tabs = Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return null;

    const childElement = child as ReactElement<any, any>;

    return cloneElement(childElement, {
      id: `custom-tab-${index}`,
      'aria-controls': `custom-tabpanel-${index}`,
      value: index,
    });
  });

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="custom tabs"
        {...other}
      >
        {tabs}
      </Tabs>
    </Box>
  );
};

// Вложенный компонент Tab
TabsRoot.Tab = function TabComponent({ children, ...other }) {
  return <Tab {...other} label={children} />;
};

// Вложенный компонент TabPanel
TabsRoot.TabPanel = function TabPanel({ children, value, ...props }) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsRoot.TabPanel must be used within a TabsRoot');
  }

  const { value: selectedValue } = context;

  return (
    <div
      role="tabpanel"
      hidden={selectedValue !== value}
      id={`custom-tabpanel-${value}`}
      aria-labelledby={`custom-tab-${value}`}
      style={{ padding: '0px', height: '100%' }}
      {...props}
    >
      {selectedValue === value && (
        <Box sx={{ py: 3, height: '100%' }}>{children}</Box>
      )}
    </div>
  );
};

export default TabsRoot;
